import React from 'react';
import { createPortal } from 'react-dom';
import { Building2, ChevronDown } from 'lucide-react';

import { LoadingSpinner } from '../../components/LoadingSpinner';
import { CONTACT_KINDS, type Contact, type ContactKind } from '../../types';

function badgeClasses(kind: ContactKind | null | undefined): string {
  return kind === 'Privatkunde'
    ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200/80'
    : 'bg-blue-100 text-blue-800 hover:bg-blue-200/80';
}

/** Clickable contact-kind badge with dropdown (no native `<select>`). */
export const ContactKindBadgePicker: React.FC<{
  value: ContactKind | null | undefined;
  contact: Contact;
  saving?: boolean;
  onSwitch: (patch: Partial<Contact> & { contact_kind: ContactKind }) => Promise<boolean> | boolean;
}> = ({ value, contact, saving, onSwitch }) => {
  const [open, setOpen] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const [localError, setLocalError] = React.useState<string | null>(null);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  const kind = value ?? null;
  const isPrivat = kind === 'Privatkunde';
  const busy = saving === true;

  const updateMenuPosition = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const maxPanel = 180;
    let left = rect.left;
    const width = Math.max(rect.width, 220);
    if (left + width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - margin - width);
    }
    if (left < margin) left = margin;
    setMenuPos({
      top: rect.bottom + 4,
      left,
      width,
      maxHeight: Math.min(maxPanel, Math.max(0, spaceBelow)),
    });
  }, []);

  React.useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
    const onScroll = (e: Event) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (panelRef.current?.contains(t)) return;
      updateMenuPosition();
    };
    window.addEventListener('resize', updateMenuPosition);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open, updateMenuPosition]);

  React.useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) setLocalError(null);
  }, [open]);

  const close = () => setOpen(false);

  const applyKind = async (next: ContactKind) => {
    if (next === kind) {
      close();
      return;
    }
    setLocalError(null);

    if (next === 'Privatkunde') {
      const ln = (contact.last_name ?? '').trim();
      if (!ln) {
        setLocalError('Nachname ist für Privatkunden erforderlich.');
        return;
      }
      const ok = await onSwitch({
        contact_kind: 'Privatkunde',
        contact_type: 'Privatkunde',
        organization_id: null,
        company: null,
      });
      if (ok) close();
      return;
    }

    const ok = await onSwitch({
      contact_kind: 'Gewerblicher Kontakt',
      contact_type: 'Gewerblicher Kunde',
    });
    if (ok) close();
  };

  const panel =
    open &&
    !busy &&
    menuPos &&
    createPortal(
      <div
        ref={panelRef}
        role="listbox"
        aria-label="Kontaktart wählen"
        style={{
          position: 'fixed',
          top: menuPos.top,
          left: menuPos.left,
          width: menuPos.width,
          maxHeight: menuPos.maxHeight,
          zIndex: 300,
        }}
        className="bg-app-surface border border-app-border rounded-lg shadow-lg overflow-hidden flex flex-col min-h-0 overscroll-y-contain"
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="py-1">
          {CONTACT_KINDS.map((t) => {
            const active = kind === t;
            return (
              <button
                key={t}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => void applyKind(t)}
                className={`w-full text-left px-3 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                  active ? 'bg-pipedrive-blue text-white' : 'text-app-text hover:bg-app-hover'
                }`}
              >
                {t === 'Gewerblicher Kontakt' ? (
                  <Building2 className="w-3.5 h-3.5 shrink-0 opacity-80" aria-hidden />
                ) : null}
                {t}
              </button>
            );
          })}
        </div>
        {localError && (
          <div className="border-t border-app-border/60 px-3 py-2 text-xs text-red-600">{localError}</div>
        )}
      </div>,
      document.body,
    );

  if (!kind) return null;

  return (
    <div className="relative shrink-0" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        disabled={busy}
        aria-expanded={open}
        aria-haspopup="listbox"
        title="Kontaktart ändern"
        onClick={() => {
          if (busy) return;
          setOpen((o) => !o);
        }}
        className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium transition-colors disabled:opacity-50 ${badgeClasses(
          kind,
        )}`}
      >
        {!isPrivat ? <Building2 className="w-3 h-3 opacity-80" aria-hidden /> : null}
        <span>{kind}</span>
        {busy ? (
          <LoadingSpinner size="xs" className="opacity-80" />
        ) : (
          <ChevronDown
            className={`w-3 h-3 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden
          />
        )}
      </button>
      {panel}
    </div>
  );
};
